import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { CheckCircle2, Circle } from "lucide-react";
import { api } from "@/lib/api";
import { PageHead } from "./Layout";

function Stat({ label, value, sub, id }: { label: string; value: React.ReactNode; sub?: string; id: string }) {
  return (
    <div className="bg-card border rounded-xl p-5" data-testid={`stat-${id}`}>
      <p className="text-xs uppercase tracking-wider text-muted-foreground">{label}</p>
      <p className="font-heading text-3xl font-bold mt-2">{value}</p>
      {sub && <p className="text-xs text-muted-foreground mt-1">{sub}</p>}
    </div>
  );
}

export default function Overview() {
  const [d, setD] = useState<any>(null);
  const nav = useNavigate();
  useEffect(() => {
    Promise.all([api.get("/orgs/me"), api.get("/orgs/me/services"), api.get("/orgs/me/usage")]).then(([o, s, u]) =>
      setD({ org: o.data, services: s.data, usage: u.data })).catch(() => nav("/app/admin", { replace: true }));
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
  if (!d) return <p className="text-muted-foreground">Loading…</p>;
  const { org, services, usage } = d;
  const checks = [
    { done: !!(org.phone || org.website), label: "Add your business contact details", to: "/app/business" },
    { done: services.some((s: any) => s.active), label: "Set your prices and publish a service", to: "/app/services" },
    { done: !!org.logo_url, label: "Upload your logo and brand color", to: "/app/appearance" },
    { done: org.entitlement?.active, label: "Activate your subscription", to: "/app/billing" },
    { done: false, label: "Copy the embed code onto your website", to: "/app/install", optional: true },
  ];
  const pct = (a: number, b: number) => (b ? Math.min(100, Math.round((a / b) * 100)) : 0);
  const email = usage.email_status || {};
  const a = usage.allowance;
  const pc = usage.period_counts;
  const day = (iso: string) => new Date(iso).toLocaleDateString("en-US", { dateStyle: "medium", timeZone: "UTC" });
  const basis = a.period_basis === "billing_period" ? "billing period" : "calendar month (UTC)";
  return (
    <div className="fade-up">
      <PageHead title={`Welcome, ${org.name}`} sub={`Current ${basis}: ${day(a.period_start)} – ${day(a.period_end)}. Allowance resets ${day(a.period_end)}.`} />
      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <Stat id="map-sessions" label="Website map sessions" value={`${a.website_used} / ${a.available}`} sub={`${pct(a.website_used, a.available)}% used · ${a.remaining} left${a.extra ? ` (incl. ${a.extra} extra)` : ""}`} />
        <Stat id="field-sessions" label="Field Quote sessions" value={a.field_used} sub="Separate — doesn’t use your website allowance" />
        <Stat id="leads" label="Website leads this period" value={pc.website_lead} sub={`${usage.leads_new} new · ${usage.leads_total} total all-time`} />
        <Stat id="emails" label="Emails this period" value={email.sent || 0} sub={`${email.failed || 0} failed · ${(email.pending || 0) + (email.retrying || 0)} pending`} />
      </div>
      {pc.reconstructed > 0 && (
        <p className="mt-3 text-xs text-muted-foreground" data-testid="usage-reconstructed-note">
          {pc.reconstructed} of this period’s counts were reconstructed from saved leads created before detailed usage tracking began{usage.ledger_started_at ? ` (${day(usage.ledger_started_at)})` : ""}. Map-load activity before then isn’t available.
        </p>
      )}
      <div className="mt-6 bg-card border rounded-xl p-6" data-testid="funnel-panel">
        <h2 className="font-bold text-lg mb-4">Homeowner funnel — this {basis}</h2>
        <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
          {[["began", "Began quote"], ["contact", "Contact submitted"], ["measuring", "Started measuring"], ["completed", "Completed estimate"], ["requested", "Requested service"]].map(([k, label]) => (
            <div key={k} className="rounded-lg bg-secondary/60 p-3" data-testid={`funnel-${k}`}>
              <p className="font-heading text-2xl font-bold">{usage.funnel[k] || 0}</p>
              <p className="text-xs text-muted-foreground">{label}</p>
            </div>
          ))}
        </div>
      </div>
      <div className="mt-10 bg-card border rounded-xl p-6" data-testid="setup-checklist">
        <h2 className="font-bold text-lg mb-4">Setup checklist</h2>
        <ul className="divide-y">
          {checks.map((c) => (
            <li key={c.label} className="py-3 flex items-center gap-3">
              {c.done ? <CheckCircle2 className="h-5 w-5 text-primary" /> : <Circle className="h-5 w-5 text-muted-foreground" />}
              <Link to={c.to} className="text-sm hover:underline">{c.label}</Link>
            </li>
          ))}
        </ul>
        {!org.entitlement?.active && <p className="text-sm text-amber-800 bg-amber-50 border border-amber-200 rounded-lg p-3 mt-4" data-testid="entitlement-warning">Your widget won’t accept homeowner requests until your subscription is active.</p>}
      </div>
    </div>
  );
}
